/* eslint-disable*/
import React, { useEffect, useState, useRef, useCallback } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faChevronLeft,
  faChevronRight,
  faDownload,
  faSearch,
  faHome,
  faSync
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { API_URL } from "../../config";
import moment from "moment";
import { addMonths, subMonths, startOfMonth } from "date-fns";
import { showToast } from "../../components/toast";
import '../../components/skeletonRow.css'

const SubscriberStatusReport = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isRendering, setIsRendering] = useState(false);
  const tableRenderedRef = useRef(false);
  const [exportLoading, setExportLoading] = useState(false);
  const prevMonthStart = startOfMonth(subMonths(new Date(), 1));

  // Separate state for form inputs and actual filters
  const [formInputs, setFormInputs] = useState({
    screated_on: null,
    ecreated_on: null
  });

  const [filters, setFilters] = useState({
    screated_on: "",
    ecreated_on: "",
    orderbycol: "trackdate",
    orderby: "desc",
    page: 1,
    limit: 100
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    showingRecords: "0 to 0 of 0"
  });

  // Refs for double scroll
  const tableContainerRef = useRef(null);
  const topScrollbarRef = useRef(null);
  const topScrollbarContentRef = useRef(null);

  // Fetch report data
  const fetchReportData = useCallback(async (pageNumber = 1) => {
    try {
      setLoading(true);
      setIsRendering(true);
      tableRenderedRef.current = false;

      const token = localStorage.getItem("authToken");

      const payload = {
        screated_on: filters.screated_on,
        ecreated_on: filters.ecreated_on,
        orderbycol: filters.orderbycol,
        orderby: filters.orderby,
        page: pageNumber - 1, // Backend expects 0-based page
        limit: filters.limit
      };

      console.log("API Payload:", payload); // Debug log

      // Remove empty values
      Object.keys(payload).forEach(key => {
        if (payload[key] === "" || payload[key] === null || payload[key] === undefined) {
          delete payload[key];
        }
      });

      const response = await axios.post(
        `${API_URL}report-subscriber-status`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "*/*",
            "Content-Type": "application/json"
          }
        }
      );

      console.log("API Response:", response.data); // Debug log

      if (response.data) {
        const data = response.data;

        // Handle response structure
        const subscriberStatusData = data.subscriberstatus || data.data || [];
        setReportData(subscriberStatusData);

        const totalRecords = data.total || subscriberStatusData.length;
        const totalPages = Math.ceil(totalRecords / filters.limit);
        const startRecord = ((pageNumber - 1) * filters.limit) + 1;
        const endRecord = Math.min(pageNumber * filters.limit, totalRecords);

        setPagination({
          currentPage: pageNumber,
          totalPages: totalPages,
          totalRecords: totalRecords,
          showingRecords: `${startRecord} to ${endRecord} of ${totalRecords}`
        });

        setTimeout(() => {
          tableRenderedRef.current = true;
          checkRenderingStatus();
        }, 100);
      }
    } catch (error) {
      console.error("Error fetching subscriber status data:", error);
      console.error("Error details:", error.response?.data); // Debug log
      setReportData([]);
      tableRenderedRef.current = true;
      checkRenderingStatus();
      showToast.error("Failed to load subscriber status data");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const checkRenderingStatus = () => {
    if (tableRenderedRef.current) {
      setTimeout(() => {
        setIsRendering(false);
      }, 300);
    }
  };

  useEffect(() => {
    fetchReportData(filters.page);
  }, [filters, fetchReportData]);

  // Double scroll implementation
  useEffect(() => {
    const updateScrollbar = () => {
      if (tableContainerRef.current && topScrollbarContentRef.current) {
        const tableWidth = tableContainerRef.current.scrollWidth;
        topScrollbarContentRef.current.style.width = `${tableWidth}px`;
      }
    };

    updateScrollbar();
    window.addEventListener('resize', updateScrollbar);
    return () => window.removeEventListener('resize', updateScrollbar);
  }, [reportData]);

  const isSyncingScrollRef = useRef(false);
  const lastTableScrollLeftRef = useRef(0);

  const syncScroll = () => {
    if (tableContainerRef.current && topScrollbarRef.current) {
      const currentLeft = tableContainerRef.current.scrollLeft;
      if (currentLeft === lastTableScrollLeftRef.current) return;
      lastTableScrollLeftRef.current = currentLeft;
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      topScrollbarRef.current.scrollLeft = currentLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    }
  };

  const handleTopScroll = () => {
    if (tableContainerRef.current && topScrollbarRef.current) {
      const currentLeft = topScrollbarRef.current.scrollLeft;
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      tableContainerRef.current.scrollLeft = currentLeft;
      lastTableScrollLeftRef.current = currentLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    }
  };

  // Handle date range changes for Created Date
  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setFormInputs(prev => ({
      ...prev,
      screated_on: start,
      ecreated_on: end
    }));
  };

  // Apply all filters (called on Enter key or search button click)
  const applyFilters = () => {
    // Format dates to "DD-MM-YYYY" before setting filters
    const formattedStartDate = formInputs.screated_on
      ? moment(formInputs.screated_on).format("DD-MM-YYYY")
      : "";

    const formattedEndDate = formInputs.ecreated_on
      ? moment(formInputs.ecreated_on).format("DD-MM-YYYY")
      : "";

    setFilters(prev => ({
      ...prev,
      screated_on: formattedStartDate,
      ecreated_on: formattedEndDate,
      page: 1
    }));
  };

  const handleSort = (column) => {
    setFilters(prev => ({
      ...prev,
      orderbycol: column,
      orderby: prev.orderbycol === column && prev.orderby === "asc" ? "desc" : "asc",
      page: 1
    }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setFilters(prev => ({ ...prev, page: newPage }));
    }
  };

  const exportReport = async () => {
    try {
      setExportLoading(true);
      const token = localStorage.getItem("authToken");

      const payload = {
        screated_on: filters.screated_on,
        ecreated_on: filters.ecreated_on
      };

      // Remove empty values
      Object.keys(payload).forEach(key => {
        if (payload[key] === "" || payload[key] === null || payload[key] === undefined) {
          delete payload[key];
        }
      });

      console.log("Export Payload:", payload); // Debug log

      const response = await axios.post(
        `${API_URL}report-subscriber-status/export`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `subscriber-status-report-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast.success("Report exported successfully.");
    } catch (error) {
      console.error("Error exporting report:", error);
      showToast.error("Failed to export report. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  const clearFilters = () => {
    setFormInputs({
      screated_on: null,
      ecreated_on: null
    });
    setFilters({
      screated_on: "",
      ecreated_on: "",
      orderbycol: "trackdate",
      orderby: "desc",
      page: 1,
      limit: 100
    });
  };

  useEffect(() => {
    const handleClearFilters = () => {
      clearFilters();
    };

    window.addEventListener('clearSubscriberStatusReportFiltersEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearSubscriberStatusReportFiltersEvent', handleClearFilters);
    };
  }, []);

  // Helper functions
  const getSortIcon = (column) => {
    if (filters.orderbycol !== column) return faSort;
    return filters.orderby === "asc" ? faSortUp : faSortDown;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "----";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "----";
      return moment(date).format("DD-MM-YYYY");
    } catch (error) {
      return "----";
    }
  };

  const hasAnyFilter = () => {
    return (
      filters.screated_on ||
      filters.ecreated_on ||
      filters.orderbycol !== "trackdate" ||
      filters.orderby !== "desc"
    );
  };

  const isPrevDisabled = pagination.currentPage <= 1;
  const isNextDisabled = pagination.currentPage >= pagination.totalPages;

  return (
    <div className="panel-content mt-5">
      {/* {(loading || isRendering || exportLoading) && <LoadingSpinner />} */}

      {/* Header with Breadcrumb and Controls */}
      <div className="panel-heading d-flex justify-content-between">
        <div className="col-md-4">
          <div style={{ float: "left", position: "relative", marginLeft: "5px" }}>
            {hasAnyFilter() && (
              <button
                className="btn btn-default btn-xs ml-1"
                style={{ marginLeft: "6px" }}
                onClick={clearFilters}
                title="Clear all filters"
              >
                Clear Filter
              </button>
            )}
          </div>
        </div>
        <div>
          <span style={{ fontSize: "0.8rem" }} className="me-3">
            {loading ? (
              <span
                className="skeleton-text"
                style={{ width: "120px", display: "inline-block" }}
              ></span>
            ) : (
              pagination.showingRecords
            )}
          </span>

          <button
            className="btn btn-default btn-sm mx-1"
            onClick={() => fetchReportData(1)}
          >
            <FontAwesomeIcon icon={faSync} />
          </button>
          <button
            className="btn btn-default btn-sm mx-1"
            onClick={exportReport}
            disabled={exportLoading || reportData.length === 0}
          >
            {exportLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-1"
                  role="status"
                  aria-hidden="true"
                ></span>
                Exporting...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faDownload} /> Export Report
              </>
            )}
          </button>
          <button
            className={`btn btn-default btn-sm ${filters.page === 1 ? "d-none" : ""}`}
            title="Previous"
            onClick={() => handlePageChange(filters.page - 1)}
            disabled={filters.page === 1}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          &nbsp;
          <button
            className={`btn btn-default btn-sm ${filters.page === pagination.totalPages ? "d-none" : ""}`}
            title="Next"
            onClick={() => handlePageChange(filters.page + 1)}
            disabled={filters.page === pagination.totalPages}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>

      {/* Main Panel */}
      <div className="panel panel-default with-rounded-corner with-shadow">
        <div className="large-table-container-3">
          {/* Top Scrollbar */}
          <div
            ref={topScrollbarRef}
            onScroll={handleTopScroll}
            style={{
              overflowX: "auto",
              overflowY: "hidden",
              height: "16px",
              marginBottom: "2px",
              background: "#f8f9fa",
            }}
          >
            <div
              ref={topScrollbarContentRef}
              style={{ height: "1px" }}
            ></div>
          </div>

          {/* Table Container */}
          <div
            ref={tableContainerRef}
            onScroll={syncScroll}
            style={{
              overflowY: "auto",
              height: "calc(112vh - 250px)",
              overflowX: "auto",
              position: "relative",
            }}
          >
            <table className="table table-condensed table-hover bg-white sticky-table">
              <thead>
                {/* Header Row */}
                <tr style={{ cursor: 'pointer' }}>
                  <th onClick={() => handleSort('trackdate')}>
                    Date <FontAwesomeIcon icon={getSortIcon('trackdate')} />
                  </th>
                  <th>CRM Total</th>
                  <th>Still In New</th>
                  <th>Contact In Future</th>
                  <th>Follow Up</th>
                  <th>Not Interested</th>
                  <th>No Response</th>
                  <th>Prospects</th>
                  <th>Subscribed</th>
                  <th>Junk</th>
                </tr>

                {/* Filter Row */}
                <tr>
                  <th>
                    <div className="d-flex align-items-center gap-1">
                      <DatePicker
                        selected={formInputs.screated_on}
                        onChange={handleDateChange}
                        startDate={formInputs.screated_on}
                        endDate={formInputs.ecreated_on}
                        selectsRange
                        isClearable
                        toggleCalendarOnIconClick
                        placeholderText=""
                        monthsShown={3}
                        openToDate={formInputs.screated_on || new Date()}
                        showYearDropdown
                        showMonthDropdown
                        dropdownMode="select"
                        scrollableYearDropdown
                        yearDropdownItemNumber={15}
                        minDate={new Date(2018, 0, 1)}
                        maxDate={new Date(2030, 11, 31)}
                        dateFormat="dd-MM-yyyy"
                      />
                      <button
                        type="button"
                        className="btn btn-default btn-xs btn-group-xs"
                        onClick={applyFilters}
                      >
                        <FontAwesomeIcon icon={faSearch} />
                      </button>
                    </div>
                  </th>
                  <th><p>&nbsp;</p></th>
                  <th><p>&nbsp;</p></th>
                  <th><p>&nbsp;</p></th>
                  <th><p>&nbsp;</p></th>
                  <th><p>&nbsp;</p></th>
                  <th><p>&nbsp;</p></th>
                  <th><p>&nbsp;</p></th>
                  <th><p>&nbsp;</p></th>
                  <th><p>&nbsp;</p></th>
                </tr>
              </thead>
              <tbody>
                {(loading || isRendering) ? (
                  // Skeleton loading rows (initial load + filter apply)
                  Array.from({ length: 10 }).map((_, skeletonIndex) => (
                    <tr key={`skeleton-${skeletonIndex}`} className="skeleton-row">
                      <td>
                        <div className="skeleton-text" style={{ width: "85%" }}></div>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <div className="skeleton-text" style={{ width: "40%", margin: "0 auto" }}></div>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <div className="skeleton-text" style={{ width: "40%", margin: "0 auto" }}></div>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <div className="skeleton-text" style={{ width: "40%", margin: "0 auto" }}></div>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <div className="skeleton-text" style={{ width: "40%", margin: "0 auto" }}></div>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <div className="skeleton-text" style={{ width: "40%", margin: "0 auto" }}></div>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <div className="skeleton-text" style={{ width: "40%", margin: "0 auto" }}></div>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <div className="skeleton-text" style={{ width: "40%", margin: "0 auto" }}></div>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <div className="skeleton-text" style={{ width: "40%", margin: "0 auto" }}></div>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <div className="skeleton-text" style={{ width: "40%", margin: "0 auto" }}></div>
                      </td>
                    </tr>
                  ))
                ) : reportData.length > 0 ? (
                  reportData.map((item, idx) => (
                    <tr key={idx}>
                      <td>{formatDate(item.trackdate)}</td>
                      <td style={{ textAlign: "center" }}>{item.total || 0}</td>
                      <td style={{ textAlign: "center" }}>{item.new || 0}</td>
                      <td style={{ textAlign: "center" }}>{item.contactinfuture || 0}</td>
                      <td style={{ textAlign: "center" }}>{item.followup || 0}</td>
                      <td style={{ textAlign: "center" }}>{item.notinterested || 0}</td>
                      <td style={{ textAlign: "center" }}>{item.noresponse || 0}</td>
                      <td style={{ textAlign: "center" }}>{item.prospect || 0}</td>
                      <td style={{ textAlign: "center" }}>{item.subscribed || 0}</td>
                      <td style={{ textAlign: "center" }}>{item.junk || 0}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    {/* No records state kept unchanged */}
                  </tr>
                )}
              </tbody>

            </table>
          </div>
        </div>
      </div>

      <style jsx>{`
        .breadcrumb {
          background: transparent;
          margin-bottom: 0;
          padding: 0;
        }

        .breadcrumb-item + .breadcrumb-item::before {
          content: ">";
        }

        .panel {
          background: white;
          border-radius: 4px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .with-rounded-corner {
          border-radius: 4px;
        }

        .with-shadow {
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .table-condensed th,
        .table-condensed td {
          padding: 8px 5px;
          font-size: 12px;
        }

        .btn-default {
          background: #f8f9fa;
          border: 1px solid #ddd;
        }

        .btn-xs {
          padding: 1px 5px;
          font-size: 12px;
          line-height: 1.5;
        }

        .form-control.input-sm {
          height: 28px;
          padding: 3px 6px;
          font-size: 12px;
        }

        th {
          white-space: nowrap;
          background: #f8f9fa;
        }

        .table-hover tbody tr:hover {
          background-color: #f5f5f5;
        }

        .badge {
          font-size: 0.7em;
        }
      `}</style>
    </div>
  );
};

export default SubscriberStatusReport;